import React, { useState, useEffect } from 'react';
import { X, Users, UserPlus, Trash2, AlertCircle, CheckCircle, Clock, PlayCircle } from 'lucide-react';
import { enqueueSnackbar } from 'notistack';
import { provasService } from '../../services/provas';
import { api } from '../../services/api';
import { ProvaAcesso, ProvaComEstatisticas } from '../../types/provas';

interface Militar {
  id: string;
  nick: string;
  patente: {
    nome: string;
  };
}

interface GerenciarAcessosModalProps {
  isOpen: boolean;
  onClose: () => void;
  prova: ProvaComEstatisticas;
}

const GerenciarAcessosModal: React.FC<GerenciarAcessosModalProps> = ({
  isOpen,
  onClose,
  prova
}) => {
  const [acessos, setAcessos] = useState<ProvaAcesso[]>([]);
  const [militaresDisponiveis, setMilitaresDisponiveis] = useState<Militar[]>([]);
  const [militaresSelecionados, setMilitaresSelecionados] = useState<string[]>([]);
  const [loading, setLoading] = useState(false);
  const [loadingConceder, setLoadingConceder] = useState(false);

  useEffect(() => {
    if (isOpen) {
      carregarDados();
    }
  }, [isOpen, prova.id]);

  const carregarDados = async () => {
    try {
      setLoading(true);

      // Carregar acessos existentes
      const acessosData = await provasService.listarAcessosPorProva(prova.id);
      setAcessos(acessosData);

      // Carregar militares do grupo
      const response = await api.get(`/grupos/${prova.id_grupo}/militares`);
      const todosMilitares = response.data.data || response.data;

      // Filtrar militares que já têm acesso
      const militaresComAcesso = acessosData.map(acesso => acesso.id_militar);
      const militaresSemAcesso = todosMilitares.filter(
        (militar: Militar) => !militaresComAcesso.includes(militar.id)
      );

      setMilitaresDisponiveis(militaresSemAcesso);
    } catch (error) {
      console.error('Erro ao carregar dados:', error);
      enqueueSnackbar('Erro ao carregar dados', { variant: 'error' });
    } finally {
      setLoading(false);
    }
  };

  const handleConcederAcessos = async () => {
    if (militaresSelecionados.length === 0) {
      enqueueSnackbar('Selecione pelo menos um militar', { variant: 'warning' });
      return;
    }

    try {
      setLoadingConceder(true);
      await provasService.concederAcessos(prova.id, militaresSelecionados);
      enqueueSnackbar(`Acesso concedido para ${militaresSelecionados.length} militar(es)`, { variant: 'success' });
      setMilitaresSelecionados([]);
      await carregarDados();
    } catch (error: any) {
      console.error('Erro ao conceder acessos:', error);
      enqueueSnackbar(error.response?.data?.message || 'Erro ao conceder acessos', { variant: 'error' });
    } finally {
      setLoadingConceder(false);
    }
  };

  const handleRevogarAcesso = async (acesso: ProvaAcesso) => {
    if (acesso.status === 'em_andamento' || acesso.status === 'finalizada') {
      enqueueSnackbar('Não é possível revogar acesso de uma prova já iniciada ou finalizada', { variant: 'warning' });
      return;
    }

    if (!confirm(`Tem certeza que deseja revogar o acesso de ${acesso.militar?.nick || 'este militar'}?`)) {
      return;
    }

    try {
      await provasService.revogarAcesso(acesso.id);
      enqueueSnackbar('Acesso revogado com sucesso', { variant: 'success' });
      await carregarDados();
    } catch (error: any) {
      console.error('Erro ao revogar acesso:', error);
      enqueueSnackbar(error.response?.data?.message || 'Erro ao revogar acesso', { variant: 'error' });
    }
  };

  const toggleMilitarSelecionado = (militarId: string) => {
    setMilitaresSelecionados(prev =>
      prev.includes(militarId)
        ? prev.filter(id => id !== militarId)
        : [...prev, militarId]
    );
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'disponivel':
        return <Clock size={16} className="text-blue-500" />;
      case 'em_andamento':
        return <PlayCircle size={16} className="text-orange-500" />;
      case 'finalizada':
        return <CheckCircle size={16} className="text-green-500" />;
      case 'expirada':
        return <AlertCircle size={16} className="text-red-500" />;
      default:
        return <Clock size={16} className="text-muted-foreground" />;
    }
  };

  const getStatusText = (status: string) => {
    switch (status) {
      case 'disponivel':
        return 'Disponível';
      case 'em_andamento':
        return 'Em andamento';
      case 'finalizada':
        return 'Finalizada';
      case 'expirada':
        return 'Expirada';
      default:
        return status;
    }
  };

  if (!isOpen) return null;

  if (loading) {
    return (
      <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
        <div className="bg-card rounded-lg p-8">
          <div className="text-center">Carregando...</div>
        </div>
      </div>
    );
  }

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-card rounded-lg max-w-4xl w-full max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between p-6 border-b border-border">
          <div>
            <h2 className="text-xl font-semibold">Gerenciar Acessos</h2>
            <p className="text-muted-foreground text-sm mt-1">{prova.titulo}</p>
          </div>
          <button
            onClick={onClose}
            className="text-muted-foreground hover:text-foreground"
          >
            <X size={24} />
          </button>
        </div>

        <div className="p-6 space-y-6">
          {/* Seção para conceder novos acessos */}
          <div>
            <h3 className="text-lg font-medium mb-4 flex items-center gap-2">
              <UserPlus size={20} />
              Conceder Acesso
            </h3>

            {militaresDisponiveis.length === 0 ? (
              <div className="text-center py-8 text-muted-foreground">
                <Users size={48} className="mx-auto mb-4 text-muted-foreground/40" />
                <p>Todos os militares do grupo já têm acesso à prova</p>
              </div>
            ) : (
              <>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3 mb-4 max-h-60 overflow-y-auto">
                  {militaresDisponiveis.map((militar) => (
                    <label
                      key={militar.id}
                      className="flex items-center space-x-3 p-3 border rounded-lg cursor-pointer hover:bg-muted"
                    >
                      <input
                        type="checkbox"
                        checked={militaresSelecionados.includes(militar.id)}
                        onChange={() => toggleMilitarSelecionado(militar.id)}
                        className="h-4 w-4 text-blue-600 focus:ring-ring border-border rounded"
                      />
                      <div>
                        <p className="font-medium text-foreground">{militar.nick}</p>
                        <p className="text-sm text-muted-foreground">{militar.patente?.nome}</p>
                      </div>
                    </label>
                  ))}
                </div>

                <div className="flex items-center justify-between">
                  <p className="text-sm text-muted-foreground">
                    {militaresSelecionados.length} militar(es) selecionado(s)
                  </p>
                  <button
                    onClick={handleConcederAcessos}
                    disabled={militaresSelecionados.length === 0 || loadingConceder}
                    className="px-4 py-2 bg-blue-500 text-white rounded-md hover:bg-blue-600 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {loadingConceder ? 'Concedendo...' : 'Conceder Acesso'}
                  </button>
                </div>
              </>
            )}
          </div>

          {/* Seção de acessos existentes */}
          <div>
            <h3 className="text-lg font-medium mb-4 flex items-center gap-2">
              <Users size={20} />
              Acessos Concedidos ({acessos.length})
            </h3>

            {acessos.length === 0 ? (
              <div className="text-center py-8 text-muted-foreground">
                <AlertCircle size={48} className="mx-auto mb-4 text-muted-foreground/40" />
                <p>Nenhum acesso foi concedido ainda</p>
              </div>
            ) : (
              <div className="space-y-3">
                {acessos.map((acesso) => (
                  <div
                    key={acesso.id}
                    className="flex items-center justify-between p-4 border border-border rounded-lg"
                  >
                    <div className="flex items-center space-x-4">
                      <div>
                        <p className="font-medium text-foreground">
                          {acesso.militar?.nick || 'Militar não encontrado'}
                        </p>
                        <p className="text-sm text-muted-foreground">
                          {acesso.militar?.patente?.nome}
                        </p>
                      </div>
                      <div className="flex items-center space-x-2">
                        {getStatusIcon(acesso.status)}
                        <span className="text-sm font-medium">
                          {getStatusText(acesso.status)}
                        </span>
                      </div>
                    </div>

                    <div className="flex items-center space-x-2">
                      {acesso.data_inicio && (
                        <span className="text-xs text-muted-foreground">
                          Iniciado: {new Date(acesso.data_inicio).toLocaleString()}
                        </span>
                      )}
                      {acesso.status === 'disponivel' && (
                        <button
                          onClick={() => handleRevogarAcesso(acesso)}
                          className="p-2 text-red-600 hover:bg-red-50 dark:hover:bg-red-900/20 rounded"
                          title="Revogar acesso"
                        >
                          <Trash2 size={16} />
                        </button>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default GerenciarAcessosModal;
